/**
 * Capacitor push notification bridge.
 * Loaded once on app start — no-ops gracefully in browser/PWA mode.
 * In native Android builds this registers FCM and saves the device token
 * to push_subscriptions so barbers/clients receive booking alerts natively.
 * iOS APNs support added once DUNS/Apple Developer account is ready.
 */

let pushReady = false;

export async function initCapacitorPush(supabase, userId) {
  if (!window.Capacitor?.isNativePlatform?.()) return;

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    const permResult = await PushNotifications.requestPermissions();
    if (permResult.receive !== 'granted') return;

    await PushNotifications.register();

    PushNotifications.addListener('registration', async ({ value: token }) => {
      pushReady = true;
      if (!userId || !token) return;
      const platform = window.Capacitor.getPlatform(); // 'android' | 'ios'
      // Store FCM token in push_subscriptions alongside web push subscriptions
      await supabase.from('push_subscriptions').upsert(
        {
          user_id: userId,
          barber_id: userId,
          fcm_token: token,
          device_platform: platform,
          // endpoint is required (unique) — use token as a stable identifier
          endpoint: `fcm:${userId}:${platform}`,
          subscription: {},
        },
        { onConflict: 'endpoint' }
      );
      // The endpoint key above only dedupes within the same user_id, but the same
      // physical device token can be left registered under a previously-logged-in
      // account (e.g. switching accounts on one phone during testing) -- that stale
      // row keeps receiving pushes meant for this device forever. Clean those up.
      await supabase.from('push_subscriptions').delete().eq('fcm_token', token).neq('user_id', userId);
    });

    // Foreground: save to notification inbox in localStorage
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      try {
        const { title, body, data } = notification;
        const inbox = JSON.parse(localStorage.getItem('faded_notif_inbox') || '[]');
        inbox.unshift({ id: Date.now(), title: title || 'FADED', body: body || '', data: data || {}, ts: Date.now(), read: false });
        if (inbox.length > 50) inbox.splice(50);
        localStorage.setItem('faded_notif_inbox', JSON.stringify(inbox));
        window.dispatchEvent(new CustomEvent('faded:notif-received'));
      } catch {}
    });

    // Tap on notification — dispatch event so App.js can switch tabs
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action.notification.data || {};
      window.dispatchEvent(new CustomEvent('faded:push-tap', { detail: data }));
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.error('[FADED] Push registration error:', err);
    });

  } catch (e) {
    console.error('[FADED] initCapacitorPush:', e);
  }
}

export function isPushReady() { return pushReady; }
